import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { Box, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import SavePDF from '@/assets/images/SavePDFIcon.svg'
import Button from '@/components/Button'
import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import Stepper from '@/components/Stepper'
import { useAppSelector } from '@/store'
import {
  useGetCaseDetailsQuery,
  useGetMetadataQuery,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import {
  setShadeGuideActiveStep,
  setShadeGuideRecipeOutput,
} from '@/store/shadeGuide'
import { isNullorEmpty } from '@/utils'
import { SHADE_GUIDE_OUTPUT_TABS, SHADE_GUIDE_STEPS } from '@/utils/constants'
import { saveRecipeGeneric } from '@/utils/helper'

import ImageContainer from './ImageContainer'
import RecipeDetailTable from './RecipeDetailTable'
import RecipeOutPdf from './RecipeOutPdf'
import RecipeTabContainer from './RecipeTabContainer'

const SaveButtonStyle = {
  padding: '8px 22px',
  fontSize: '15px',
  fontWeight: '500',
  letterSpacing: '0.46px',
  lineHeight: '26px',
  display: 'flex',
  gap: '8px',
  '& .MuiButton-startIcon': {
    margin: '0px',
  },
}

function ShadeGuideAiOutput() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'allInOne.recipeOutput',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })

  const [tableKey, setTableKey] = useState<any>()
  const [selectedTab, setSelectedTab] = useState<string>('')
  const caseId = new URLSearchParams(useLocation().search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()
  const { data: caseDetails } = useGetCaseDetailsQuery(
    { caseId },
    { skip: !caseId },
  )

  const recipeOutput = useAppSelector(
    (state) => state.shadeGuide.shadeGuideRecipeOutput,
  )
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const restorationMethodOnRecipeGenerate = useSelector(
    (state: any) => state.shadeGuide.restorationMethodOnRecipeGenerate,
  )

  const dispatch = useDispatch()
  const { state }: any = useLocation()

  const theme = useTheme()
  const navigate = useNavigate()

  const { data: metaData } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: any | undefined }

  const providedInputNames = metaData?.provided_input_names

  useEffect(() => {
    if (!recipeOutput) return

    const { aestheticmodel_middle, enameloptions_middle, provided_input } =
      recipeOutput

    if (aestheticmodel_middle) {
      setSelectedTab('Stump Shade')
    } else if (restorationMethodOnRecipeGenerate === 'staining') {
      setSelectedTab('Staining')
    } else if (restorationMethodOnRecipeGenerate === 'layering') {
      setSelectedTab('Wash Bake')
    } else if (enameloptions_middle) {
      setSelectedTab('Enamel')
    } else {
      setSelectedTab('Dentin')
    }

    getKey(provided_input)
  }, [recipeOutput, restorationMethodOnRecipeGenerate])

  useEffect(() => {
    if (isNullorEmpty(state, 'recipe')) {
      dispatch(setShadeGuideRecipeOutput(state.recipe))
    }
  }, [])

  const getKey = (input: any) => {
    if (!input) return
    const excludedKeys =
      input?.recipe_type === 'single dentin'
        ? [
            'prepcolor_cervical',
            'prepcolor_middle',
            'prepcolor_incisal',
            'availspace_cervical',
            'availspace_incisal',
            'targetcolor_cervical',
            'targetcolor_incisal',
          ]
        : [
            'skinenameltype_cervical',
            'skinenameltype_incisal',
            'method',
            'portionertype',
          ]

    setTableKey(Object.keys(input).filter((key) => !excludedKeys.includes(key)))
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: recipeOutput,
      workflow: null,
      mutationFn: saveRecipe,
      recipeType: recipeOutput?.recipe_type,
      closeDialog: () => setSaveRecipedialogOpen(false),
    })
  }

  const generatePdf = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: recipeOutput,
      workflow: null,
      mutationFn: savePdf,
      recipeType: recipeOutput?.recipe_type,
      isPdf: true,
    })
  }

  const handleStepClick = (index: number) => {
    if (index === 0 || index === 1) {
      navigate(`/shade-guide-ai?caseId=${caseId}`, {
        state: { fromOutput: true },
      })
      dispatch(setShadeGuideActiveStep(index))
    }
  }

  const handleBackClick = () => {
    navigate(`/projects/${caseId}`)
  }

  return (
    <>
      <Box display="flex" flexGrow={1} flexDirection="column" zIndex={1111}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
          py={2}
          sx={{
            position: 'relative',
          }}
        >
          {caseDetails?.case_type !== 'auto' && (
            <Box
              sx={{
                position: 'absolute',
                left: 32,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                variant="text"
                onClick={handleBackClick}
                sx={{
                  color: '#90CAF9',
                  fontSize: '15px',
                  fontWeight: 500,
                  lineHeight: '26px',
                }}
                label={t('backToProject')}
                startIcon={<ChevronLeftIcon />}
                textTransform="uppercase"
              />
            </Box>
          )}

          <Box display={'flex'} flexGrow={1}>
            <Stepper
              activeStep={3}
              steps={SHADE_GUIDE_STEPS}
              onStepClick={handleStepClick}
            />
          </Box>
        </Box>
        <Box
          sx={{
            height: 'calc(100% - 80px)',
            backgroundColor: theme.palette.background.paper,
            overflowY: 'auto',
            paddingX: 6,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '22px',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={'16px'}
              py={'24px'}
            >
              <Box
                sx={{ color: 'white', fontSize: '24px', lineHeight: '20px' }}
              >
                {t('yourRecipe')}
              </Box>
              <Box display="flex" gap={'16px'} flexWrap="wrap">
                <Button
                  label={translate('saveAsPdf')}
                  variant="outlined"
                  startIcon={<img src={SavePDF} alt="savePDF" />}
                  onClick={async () => generatePdf()}
                  sx={SaveButtonStyle}
                  textTransform="uppercase"
                />
                <Button
                  label={translate('saveToProject')}
                  startIcon={<CloudDownloadIcon />}
                  sx={SaveButtonStyle}
                  onClick={async () => handleOpenSaveDialog()}
                  textTransform="uppercase"
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 6,
                height: '484px',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background:
                    'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                  padding: '32px 16px',
                  borderRadius: 4,
                }}
              >
                {(canvasImage?.calibrated_image || canvasImage?.image) && (
                  <img
                    src={canvasImage?.calibrated_image || canvasImage?.image}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      margin: 'auto',
                      height: 'auto',
                      width: '618px',
                      filter: 'none',
                    }}
                    alt="recipeimage"
                  />
                )}
              </Box>

              <RecipeDetailTable
                tableKey={tableKey}
                providedInputNames={providedInputNames}
                recipeOutput={recipeOutput}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pb={'24px'}
            pt={'32px'}
            px={'16px'}
          >
            <Typography
              sx={{ color: 'white', fontSize: 24, lineHeight: '20px' }}
            >
              {t('recipeDetail')}
            </Typography>
          </Box>
          <Box
            sx={{
              margin: 'auto',
              maxHeight: 'fit-content',
              // height: 'max(100%, 561px)',
              background: 'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
              borderRadius: '16px',
              padding: '32px',
            }}
          >
            <Box
              display="flex"
              height="100%"
              sx={{
                '@media (max-width: 1080px)': {
                  flexDirection: 'column',
                },
              }}
            >
              <ImageContainer selectedTab={selectedTab} />
              <RecipeTabContainer
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                restorationMethodOnRecipeGenerate={
                  restorationMethodOnRecipeGenerate
                }
                recipeOutput={recipeOutput}
                options={SHADE_GUIDE_OUTPUT_TABS}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {recipeOutput && (
        <RecipeOutPdf
          recipeOutput={recipeOutput}
          tableKey={tableKey}
          providedInputNames={providedInputNames}
          restorationMethodOnRecipeGenerate={restorationMethodOnRecipeGenerate}
        />
      )}
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </>
  )
}
export default ShadeGuideAiOutput
